
import { Component, Prop, Vue } from 'vue-property-decorator';
import { cloneDeep } from 'lodash';
import { FONTS } from '@/utils/constants';
import Autocomplete from '@/components/shared/Autocomplete.vue';

@Component<CustomFont>({
	components: {
		Autocomplete
	}
})
export default class CustomFont extends Vue {
	@Prop({ type: Object, default: () => {} }) private targetMenuConfig!: MenuConfiguration;

	private mutableMenuConfiguration: MenuConfiguration = cloneDeep(this.targetMenuConfig);
	private fonts: string[] = FONTS;

	private created(): void {
		this.initializeFonts();
	}

	/**
	 * Initializes custom fonts if they are set
	 *
	 * @return {void}
	 */
	private initializeFonts(): void {
		const { primary_font, secondary_font, use_default_fonts } = this.mutableMenuConfiguration;
		if (!use_default_fonts) {
			if (primary_font) {
				this.importFont(primary_font, 'primary-font-preview');
			}
			if (secondary_font) {
				this.importFont(secondary_font, 'secondary-font-preview');
			}
		}
	}

	/**
	 * Resets the font configuration to default when the switch is toggled off
	 *
	 * @return {void}
	 */
	private useDefaultFonts(): void {
		if (this.mutableMenuConfiguration.use_default_fonts) {
			this.mutableMenuConfiguration.primary_font = '';
			this.mutableMenuConfiguration.secondary_font = '';
			document.documentElement.style.removeProperty('--primary-font-preview');
			document.documentElement.style.removeProperty('--secondary-font-preview');

			this.$emit('update-menu-config', {
				use_default_fonts: true,
				primary_font: '',
				secondary_font: ''
			});
		}
		else {
			this.$emit('update-menu-config', { use_default_fonts: false });
		}
	}

	/**
	 * Handles font selection change from dropdown.
	 * Updates the font preview and notifies parent component.
	 *
	 * @param {string} fontType - 'primary' or 'secondary'
	 * @return {void}
	 */
	private updateFont(fontType: 'primary' | 'secondary'): void {
		const fontKey = `${fontType}_font` as 'primary_font' | 'secondary_font';
		const fontValue = this.mutableMenuConfiguration[fontKey];
		const querySelector = `${fontType}-font-preview`;

		if (fontValue) {
			this.importFont(fontValue, querySelector);
		}

		this.$emit('update-menu-config', { [fontKey]: fontValue });
	}

	/**
	 * Imports a font from Google Fonts and applies it to the document for preview
	 *
	 * @param {string} font
	 * @param {string} querySelector - The ID of the link element and CSS variable to set
	 * @return {void}
	 */
	private importFont(font: string, querySelector: string): void {
		const linkElem = document.querySelector(`link[id='${querySelector}']`);
		if (linkElem) {
			document.head.removeChild(linkElem);
		}

		// Create the URL for the Google Fonts API
		const fontNoSpace = font.replace(/\s/g, '+');
		const url = `https://fonts.googleapis.com/css?family=${fontNoSpace}`;

		// Create and set the attributes for the link element
		const file = document.createElement('link');
		file.id = querySelector;
		file.rel = 'stylesheet';
		file.href = url;

		// Set the font in the document and update the CSS variable
		document.head.appendChild(file);
		document.documentElement.style.setProperty(`--${querySelector}`, font);
	}
}
